import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import {
  getPrimaryPrice,
  getProductPriceCategory,
  getSecondPrice,
  isWebSellable,
  getPrimaryPriceUnitSlug,
  getAdditionalPriceUnitSlug,
  getAdditionalPriceUnitName,
  isLaminateCalculator,
  getPreviousAdditionalPrice,
} from 'features/product-info/ducks/selectors';
import { getLaminateConfigurationArea } from 'features/configurations/ducks/selectors';
import { getGoodsSupplyCase } from 'features/goods-supply/selectors';
import { UOMS } from 'features/product-info/constants';
import { RootReducer } from 'store/root-reducer';
import { PriceMainProps } from 'common/components/product-price-view/types';
import { getLocale } from 'utils';
import { PriceViewMappedState, PriceViewProps } from './types';
import { getProjectPriceResult, isValues, setProps } from './utils';
import { APriceView } from './a-price-view';
import { FirstPriceView } from './components';
import { PdpProjectBoxCount } from '../pdp-project-box-count';

export const PriceView: React.FC<
  PriceViewProps & {
    isStickyPanel: boolean;
    secondPriceClassName?: string;
    inBasket: boolean;
    quantity: number;
  }
> = ({
  className = '',
  secondPriceClassName = className,
  firstPrice,
  secondPrice,
  isSellable,
  supplyCase,
  productPriceCategory,
  isStickyPanel,
  mainUom,
  additionalUom,
  additionalUomRus,
  configurationArea,
  isLaminateCalc,
  previousAdditionalPrice,
  inBasket,
  quantity,
  ...htmlProps
}) => {
  let content: React.ReactNode = null;
  const isBestPrice = productPriceCategory === 'bestPrice';
  const hasThreePrices = !!firstPrice.prevPrice && !!secondPrice.price;
  const locale = getLocale();

  const isCorrectUomForLaminateProjectPrice =
    (mainUom === UOMS.CAR && additionalUom === UOMS.M2) ||
    (mainUom === UOMS.M2 && additionalUom === UOMS.CAR);

  const isShowProjectPrice =
    Boolean(configurationArea) && isCorrectUomForLaminateProjectPrice && isLaminateCalc;

  const { projectPrice, areaPerBox, amountBox } = useMemo(
    () =>
      getProjectPriceResult({
        inBasket,
        quantity,
        mainUom,
        configurationArea,
        previousAdditionalPrice,
        firstPrice,
        secondPrice,
      }),
    [configurationArea, firstPrice, inBasket, mainUom, previousAdditionalPrice, quantity, secondPrice],
  );

  if (isSellable) {
    content = (
      <>
        <FirstPriceView
          price={firstPrice}
          className={className}
          isBestPrice={isBestPrice}
          hasThreePrices={hasThreePrices}
          isStickyPanel={isStickyPanel}
          {...htmlProps}
        />
        {isValues(secondPrice) ? (
          <APriceView
            hasThreePrices={hasThreePrices}
            isStickyPanel={isStickyPanel}
            className={secondPriceClassName}
            name="second"
            {...setProps({ ...secondPrice, locale })}
            {...htmlProps}
          />
        ) : null}
      </>
    );

    if (supplyCase !== null && ['CASE_1', 'CASE_5'].includes(supplyCase)) {
      content = null;
    }

    if (isShowProjectPrice) {
      content = (
        <>
          <FirstPriceView
            price={projectPrice}
            className={className}
            isBestPrice={isBestPrice}
            hasThreePrices={hasThreePrices}
            isStickyPanel={isStickyPanel}
            {...htmlProps}
          />
          <PdpProjectBoxCount
            additionalUomRus={additionalUomRus}
            mainUnit={firstPrice.unit}
            areaPerBox={areaPerBox}
            amountBox={amountBox}
          />
        </>
      );
    }
  }

  return <>{content}</>;
};

export const mapStoreToProps = (store: RootReducer): PriceViewMappedState => {
  const firstPrice = getPrimaryPrice(store) as PriceMainProps;
  const secondPrice = getSecondPrice(store) as PriceMainProps;
  const isSellable = isWebSellable(store);
  const supplyCase = getGoodsSupplyCase(store);
  const productPriceCategory = getProductPriceCategory(store) ?? '';
  const mainUom = getPrimaryPriceUnitSlug(store);
  const additionalUom = getAdditionalPriceUnitSlug(store);
  const additionalUomRus = getAdditionalPriceUnitName(store);
  const configurationArea = getLaminateConfigurationArea(store);
  const isLaminateCalc = isLaminateCalculator(store);
  const previousAdditionalPrice = getPreviousAdditionalPrice(store);

  return {
    firstPrice,
    secondPrice,
    isSellable,
    supplyCase,
    productPriceCategory,
    mainUom,
    additionalUom,
    additionalUomRus,
    configurationArea,
    isLaminateCalc,
    previousAdditionalPrice,
  };
};

export const PdpPriceView = connect(mapStoreToProps, null, (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...ownProps,
}))(PriceView);
